/**
 * ReviewPage - 評価レビュー画面
 *
 * 上段: 指摘 ⇔ 文字起こしハイライト連動ビュー（frontend/ の ManagerReviewView から移植）
 *   - 主発言は常に赤でハイライト、関連発言（supporting）は選択中のカードに紐づくもののみ amber 表示
 *   - 選択中の発話は省略解除で全文表示
 *   - 指摘番号と文字起こし左の番号バッジが連動
 *   - section_display_name バッジは削除済み
 *
 * 下段: 指摘単位の OK/NG 判定 / スキップフロー（レビュアー割り当て・状態表示）
 *
 * バックエンド API:
 *   - GET /sessions/{id}                  : 既存（セッション概要 + reviewers）
 *   - GET /sessions/{id}/review           : 新規（ReviewSessionDetailResponse、B 系レーンで実装予定）
 *   - POST /sessions/{id}/review/complete : 既存
 *   - POST /sessions/{id}/review/skip     : 既存
 *   - POST /sessions/{id}/review/reopen   : 既存完了/スキップの再開
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuthStore } from '@/state/authStore';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingState } from '@/components/Spinner';
import type { SessionStatus } from '@/types/session';
import { ReviewTranscriptPanel } from './ReviewTranscriptPanel';
import { useReviewSession } from './useReviewSession';
import { MediaPlaybackPanel } from '@/components/review/MediaPlaybackPanel';
import { SessionMeetingSummary } from '@/components/review/SessionMeetingSummary';
import { useMediaPlayer, type MediaKind } from '@/hooks/useMediaPlayer';
import { formatMeetingType } from '@/utils/meetingType';
import {
  reviewTranscriptBlocksToTimeline,
  type ReviewAdditionalFeedback,
  type ReviewItemJudgmentValue,
} from '@/types/review';

interface Reviewer {
  reviewer_user_id: string;
  reviewer_name: string;
  state: string;
}

interface ItemReviewDraft {
  judgment?: ReviewItemJudgmentValue;
  comment: string;
}

interface SessionMediaUrlResponse {
  url: string;
  kind: MediaKind;
  content_type?: string | null;
  filename?: string | null;
  role?: string | null;
  source?: string | null;
  expires_in: number;
}

function buildReviewDraftPayload(
  feedbackItems: Array<{ feedback_key: string }>,
  itemReviews: Record<string, ItemReviewDraft>,
  overallCommentBody: string,
) {
  return {
    item_reviews: feedbackItems.map((item) => {
      const draft = itemReviews[item.feedback_key];
      const comment = draft?.comment.trim();
      return {
        feedback_key: item.feedback_key,
        judgment: draft?.judgment ?? null,
        comment_body: comment || null,
      };
    }),
    overall_comment_body: overallCommentBody.trim() || null,
  };
}

type AdditionalFeedbackCreateInput = Omit<
  ReviewAdditionalFeedback,
  'id' | 'author_name' | 'created_at' | 'can_delete' | 'can_edit' | 'reviewer_user_id'
>;

export function ReviewPage() {
  const { sessionId } = useParams();
  const user = useAuthStore((s) => s.currentUser);

  const [session, setSession] = useState<Record<string, unknown> | null>(null);
  const [reviewers, setReviewers] = useState<Reviewer[]>([]);
  const [itemReviews, setItemReviews] = useState<Record<string, ItemReviewDraft>>({});
  const [additionalFeedbacks, setAdditionalFeedbacks] = useState<ReviewAdditionalFeedback[]>([]);
  const [overallCommentBody, setOverallCommentBody] = useState('');
  const [deletedAdditionalFeedbackIds, setDeletedAdditionalFeedbackIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [draftSaveError, setDraftSaveError] = useState<string | null>(null);

  const {
    detail: reviewDetail,
    loading: reviewDetailLoading,
    error: reviewDetailError,
  } = useReviewSession(sessionId);

  // メディア再生（アップロード済み S3 メディアを Presigned URL 経由で自動ロード）
  const audioTimelineBlocks = useMemo(() => {
    if (!reviewDetail) return [];
    return reviewTranscriptBlocksToTimeline(
      reviewDetail.transcript_blocks,
      reviewDetail.session_feedback_items,
    );
  }, [reviewDetail]);
  const [mediaState, mediaControls] = useMediaPlayer(audioTimelineBlocks);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [followAudioPlayback, setFollowAudioPlayback] = useState(false);
  const loadedMediaSessionIdRef = useRef<string | null>(null);
  const mediaRequestSessionIdRef = useRef<string | null>(null);
  const loadMediaUrl = mediaControls.loadUrl;
  const resetMedia = mediaControls.reset;
  useEffect(() => {
    setMediaError(null);
    loadedMediaSessionIdRef.current = null;
    mediaRequestSessionIdRef.current = null;
    resetMedia();
  }, [sessionId, resetMedia]);

  useEffect(() => {
    if (
      !sessionId ||
      mediaRequestSessionIdRef.current === sessionId ||
      loadedMediaSessionIdRef.current === sessionId ||
      audioTimelineBlocks.length === 0
    ) return;
    let cancelled = false;
    mediaRequestSessionIdRef.current = sessionId;
    api
      .get<SessionMediaUrlResponse>(`/sessions/${sessionId}/media-url`)
      .then((r) => {
        if (cancelled) return;
        if (r.data?.url) {
          const kind = r.data.kind === 'video' ? 'video' : 'audio';
          loadMediaUrl(r.data.url, r.data.filename ?? (kind === 'video' ? '会議動画' : '会議音声'), kind);
          loadedMediaSessionIdRef.current = sessionId;
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 404) setMediaError('再生可能なメディアがまだアップロードされていません');
        else setMediaError('メディアの取得に失敗しました');
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, audioTimelineBlocks.length, loadMediaUrl]);

  const refreshSession = useCallback(async () => {
    if (!sessionId) return;
    const sessResp = await api.get(`/sessions/${sessionId}`);
    setSession(sessResp.data);
    setReviewers(sessResp.data.reviewers ?? []);
  }, [sessionId]);

  useEffect(() => {
    refreshSession()
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [refreshSession]);

  const myReviewer = reviewers.find((r) => r.reviewer_user_id === String(user?.id));
  const canAct = myReviewer && ['pending', 'in_progress'].includes(myReviewer.state);
  const canReopen =
    myReviewer &&
    ['completed', 'skipped'].includes(myReviewer.state) &&
    ['feedback_pending', 'feedback_in_progress'].includes(String(session?.status ?? ''));
  const showSkip = canAct && reviewers.length >= 2;
  const isAdminPreview = Boolean(user?.is_admin && !myReviewer);
  const feedbackItems = useMemo(
    () => reviewDetail?.session_feedback_items ?? [],
    [reviewDetail?.session_feedback_items],
  );
  const judgedCount = feedbackItems.filter((item) => itemReviews[item.feedback_key]?.judgment).length;
  const persistedAdditionalFeedbacks = useMemo(
    () =>
      (reviewDetail?.additional_feedbacks ?? []).filter(
        (item) => !deletedAdditionalFeedbackIds.has(item.id),
      ),
    [deletedAdditionalFeedbackIds, reviewDetail?.additional_feedbacks],
  );
  const reviewDetailForPanel = useMemo(() => {
    if (!reviewDetail) return null;
    return {
      ...reviewDetail,
      additional_feedbacks: persistedAdditionalFeedbacks,
    };
  }, [persistedAdditionalFeedbacks, reviewDetail]);
  const hasMeetingSummary = Boolean(reviewDetail?.session.meeting_summary?.trim());
  const additionalFeedbackCount =
    persistedAdditionalFeedbacks.length + additionalFeedbacks.length;
  const ownAdditionalFeedbackCount =
    additionalFeedbacks.length + persistedAdditionalFeedbacks.filter((item) => item.can_delete).length;
  const allJudged =
    feedbackItems.length > 0
      ? judgedCount === feedbackItems.length
      : ownAdditionalFeedbackCount > 0;

  useEffect(() => {
    if (!reviewDetail) return;
    const next: Record<string, ItemReviewDraft> = {};
    for (const item of reviewDetail.session_feedback_items) {
      next[item.feedback_key] = {
        judgment: item.my_judgment ?? undefined,
        comment: item.my_comment_body ?? '',
      };
    }
    setItemReviews(next);
    setAdditionalFeedbacks([]);
    setOverallCommentBody(reviewDetail.my_overall_comment_body ?? '');
    setDeletedAdditionalFeedbackIds(new Set());
    setDraftSaveError(null);
  }, [reviewDetail]);

  const saveReviewDraft = useCallback(
    async (
      nextItemReviews: Record<string, ItemReviewDraft>,
      nextOverallCommentBody: string,
    ) => {
      if (!canAct || !sessionId || !reviewDetail) return;
      try {
        await api.post(
          `/sessions/${sessionId}/review/draft`,
          buildReviewDraftPayload(feedbackItems, nextItemReviews, nextOverallCommentBody),
        );
        setDraftSaveError(null);
      } catch {
        setDraftSaveError('入力内容の保存に失敗しました');
      }
    },
    [canAct, feedbackItems, reviewDetail, sessionId],
  );

  const setItemJudgment = (feedbackKey: string, judgment: ReviewItemJudgmentValue) => {
    const next = {
      ...itemReviews,
      [feedbackKey]: {
        comment: itemReviews[feedbackKey]?.comment ?? '',
        judgment,
      },
    };
    setItemReviews(next);
    void saveReviewDraft(next, overallCommentBody);
  };

  const setItemComment = (feedbackKey: string, comment: string) => {
    setItemReviews((prev) => ({
      ...prev,
      [feedbackKey]: {
        judgment: prev[feedbackKey]?.judgment,
        comment,
      },
    }));
  };

  const commitItemComment = (feedbackKey: string) => {
    const current = itemReviews[feedbackKey];
    if (!current) return;
    void saveReviewDraft(itemReviews, overallCommentBody);
  };

  const commitOverallComment = () => {
    void saveReviewDraft(itemReviews, overallCommentBody);
  };

  const createAdditionalFeedback = async (
    feedback: AdditionalFeedbackCreateInput,
  ): Promise<ReviewAdditionalFeedback> => {
    if (!sessionId) throw new Error('セッションIDがありません');
    const resp = await api.post<ReviewAdditionalFeedback>(
      `/sessions/${sessionId}/review/additional-feedbacks`,
      feedback,
    );
    return resp.data;
  };

  const deleteAdditionalFeedback = async (feedbackId: string): Promise<void> => {
    if (!sessionId) throw new Error('セッションIDがありません');
    await api.delete(`/sessions/${sessionId}/review/additional-feedbacks/${feedbackId}`);
    setDeletedAdditionalFeedbackIds((current) => {
      const next = new Set(current);
      next.add(feedbackId);
      return next;
    });
  };

  const updateAdditionalFeedback = async (
    feedbackId: string,
    commentBody: string,
  ): Promise<ReviewAdditionalFeedback> => {
    if (!sessionId) throw new Error('セッションIDがありません');
    const resp = await api.patch<ReviewAdditionalFeedback>(
      `/sessions/${sessionId}/review/additional-feedbacks/${feedbackId}`,
      { comment_body: commentBody },
    );
    return resp.data;
  };

  const handleComplete = async () => {
    setActionError(null);
    if (!allJudged) {
      setActionError(
        feedbackItems.length > 0
          ? 'すべてのAI指摘にOK/NGを付けてください'
          : 'AI指摘がない場合は追加コメントを1件以上登録してください',
      );
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/sessions/${sessionId}/review/complete`, {
        item_reviews: feedbackItems.map((item) => ({
          feedback_key: item.feedback_key,
          judgment: itemReviews[item.feedback_key]!.judgment,
          comment_body: itemReviews[item.feedback_key]?.comment.trim() || null,
        })),
        overall_comment_body: overallCommentBody.trim() || null,
      });
      await refreshSession();
    } catch (e: unknown) {
      setActionError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (!confirm('このセッションのフィードバックをスキップしますか？')) return;
    setActionError(null);
    setSubmitting(true);
    try {
      await api.post(`/sessions/${sessionId}/review/skip`);
      await refreshSession();
    } catch (e: unknown) {
      setActionError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReopen = async () => {
    setActionError(null);
    setSubmitting(true);
    try {
      await api.post(`/sessions/${sessionId}/review/reopen`);
      setReviewers((current) =>
        current.map((r) =>
          r.reviewer_user_id === String(user?.id) ? { ...r, state: 'in_progress' } : r,
        ),
      );
      setSession((current) =>
        current ? { ...current, status: 'feedback_in_progress' } : current,
      );
    } catch (e: unknown) {
      setActionError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><LoadingState /></div>;
  if (!session)
    return <div className="p-8 text-center text-red-500">セッションが見つかりません</div>;

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-[1920px] flex-col">
      <div className="shrink-0">
        <div className="mb-3">
          <Link to="/dashboard" className="text-sm text-blue-600 hover:underline">
            ← ダッシュボード
          </Link>
        </div>
        <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-start">
          <div className="min-w-0 shrink-0 lg:max-w-sm">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-semibold text-gray-900">フィードバック</h1>
              <StatusBadge status={session.status as SessionStatus} />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {session.client_name as string} / {session.coach_name as string} —{' '}
              {formatMeetingType(session.day_type as string)}
            </p>
          </div>
          <SessionMeetingSummary
            summary={reviewDetail?.session.meeting_summary}
            className="w-full lg:flex-1"
          />
        </div>
      </div>

      {/* メディア再生バー（アップロード済み S3 メディアを自動ロード） */}
      {reviewDetail && audioTimelineBlocks.length > 0 && (
        <MediaPlaybackPanel
          state={mediaState}
          controls={mediaControls}
          loadError={mediaError}
          className="shrink-0"
        />
      )}

      {/* 指摘 ⇔ 文字起こしハイライト連動ビュー */}
      <div
        className={`mt-4 flex ${
          hasMeetingSummary ? 'h-[calc(100vh-22rem)] min-h-[30rem]' : 'h-[calc(100vh-18rem)] min-h-[34rem]'
        } max-h-[56rem] min-w-0 flex-col`}
      >
        {reviewDetailLoading ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white p-12">
            <LoadingState message="フィードバック詳細を読み込み中..." />
          </div>
        ) : reviewDetailError ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-amber-200 bg-amber-50 p-8 text-sm text-amber-800">
            {reviewDetailError}
          </div>
        ) : reviewDetailForPanel ? (
          <ReviewTranscriptPanel
            detail={reviewDetailForPanel}
            canEditReviews={Boolean(canAct)}
            reviewDrafts={itemReviews}
            onItemJudgmentChange={setItemJudgment}
            onItemCommentChange={setItemComment}
            onItemCommentCommit={commitItemComment}
            additionalFeedbacks={additionalFeedbacks}
            onAdditionalFeedbacksChange={setAdditionalFeedbacks}
            onAdditionalFeedbackCreate={createAdditionalFeedback}
            onAdditionalFeedbackUpdate={updateAdditionalFeedback}
            onAdditionalFeedbackDelete={deleteAdditionalFeedback}
            showAdditionalFeedback
            onUtteranceClick={
              mediaState.isLoaded ? mediaControls.seekToUtterance : undefined
            }
            activeAudioUtteranceId={mediaState.activeUtteranceId}
            isAudioPlaying={mediaState.isPlaying}
            followAudioPlayback={followAudioPlayback}
            onFollowAudioPlaybackChange={setFollowAudioPlayback}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white p-12 text-sm text-gray-400">
            フィードバック詳細が取得できませんでした
          </div>
        )}
      </div>

      {/* レビュー完了操作 */}
      <div className="mt-6 min-w-0">
        <h3 className="text-sm font-semibold text-gray-700">フィードバック完了</h3>
        <p className="mt-1 text-xs text-gray-500">
          各AI指摘カード内でOK/NGとコメントを入力してください。必要に応じて発言単位の追加コメントも登録できます。
        </p>

        {!canAct && myReviewer && (
          <div className="mt-3 rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
            このセッションのフィードバックは完了済みです（{myReviewer.state}）
            {canReopen && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={handleReopen}
                  disabled={submitting}
                  className="rounded-lg border border-blue-300 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                >
                  {submitting ? '送信中...' : 'フィードバックを再開'}
                </button>
              </div>
            )}
          </div>
        )}

        {!myReviewer && (
          <div className="mt-3 rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
            {isAdminPreview
              ? '管理者プレビューです。コメントやOK/NG判定などのアクションはできません。'
              : 'このセッションのレビュアーとして割り当てられていません'}
          </div>
        )}

        {canAct && (
          <div className="mt-3 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm">
          判定済み: <span className="font-semibold">{judgedCount}</span> / {feedbackItems.length}
              {additionalFeedbackCount > 0 && (
                <span className="ml-3 text-xs text-blue-600">
                  追加コメント {additionalFeedbackCount}件
                </span>
              )}
            </div>
            {draftSaveError && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {draftSaveError}
              </p>
            )}
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                セッション全体に対するコメント
              </span>
              <textarea
                value={overallCommentBody}
                onChange={(event) => setOverallCommentBody(event.target.value)}
                onBlur={commitOverallComment}
                maxLength={10000}
                rows={4}
                className="mt-2 w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleComplete}
                disabled={submitting || !allJudged}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? '送信中...' : 'フィードバック完了'}
              </button>
              {showSkip && (
                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={submitting}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  フィードバックしない
                </button>
              )}
            </div>
          </div>
        )}

        {actionError && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {actionError}
          </div>
        )}

        <div className="mt-6">
          <h4 className="text-xs font-semibold text-gray-500">フィードバック状況</h4>
          <div className="mt-2 space-y-1">
            {reviewers.map((r) => (
              <div
                key={r.reviewer_user_id}
                className="flex items-center justify-between rounded bg-white p-2 text-sm shadow-sm"
              >
                <span className="text-gray-700">{r.reviewer_name}</span>
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    r.state === 'completed'
                      ? 'bg-green-100 text-green-700'
                      : r.state === 'skipped'
                        ? 'bg-gray-100 text-gray-500'
                        : r.state === 'in_progress'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-orange-100 text-orange-700'
                  }`}
                >
                  {r.state === 'completed'
                    ? '完了'
                    : r.state === 'skipped'
                      ? 'スキップ'
                      : r.state === 'in_progress'
                        ? 'フィードバック中'
                        : '未着手'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { code?: string; message?: string; detail?: unknown } } })
    .response?.data;
  const detail =
    data?.detail && typeof data.detail === 'object'
      ? (data.detail as { code?: unknown; message?: unknown })
      : null;
  if (data?.code === 'review.all_skipped_not_allowed' || detail?.code === 'review.all_skipped_not_allowed') {
    return '全員が「フィードバックしない」で完了することはできません。少なくとも1名はフィードバックを完了してください。';
  }
  if (data?.message) return data.message;
  if (typeof data?.detail === 'string') return data.detail;
  if (typeof detail?.message === 'string') {
    return detail.message;
  }
  return 'エラーが発生しました';
}
